import { inject, injectable, optional } from "inversify";
import * as THREE from "three";
import { Logger } from "@designboticteam/core";
import { MapConfigService, MapLibreInstanceService } from "@designboticteam/maplibre";
import { CAMERA_CONTROL_SERVICE, CameraService, type ICameraControlService } from "@designboticteam/workspace-3d";
import { EARTH_CIRCUMFERENCE_IN_METERS, METERS_PER_LAT_DEG } from "./constants";
import type { IMapLibreCameraSyncService } from "./ICameraSyncService";
import { getMapCameraOrientation } from "./utils/getMapCameraOrientation";
import { getMapCameraTarget } from "./utils/getMapCameraTarget";
import { getMetersPerLongitude } from "./utils/getMetersPerLongitude";
import { getWorkspace3DCameraOrientationVectors } from "./utils/getWorkspace3DCameraOrientationVectors";

@injectable()
class Workspace3DToMapCameraSyncService implements IMapLibreCameraSyncService {
	public readonly syncDirection = "workspace-3d-to-map" as const;
	public readonly serviceName = "🗺️ Workspace3DToMapLibreCameraSyncService";
	private logger = new Logger(`🔗 CONNECTOR | ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#6644aa" });

	private metersPerLngDeg = 0;

	constructor(
		@inject(MapConfigService) private mapConfigService: MapConfigService,
		@inject(MapLibreInstanceService) private mapLibreInstanceService: MapLibreInstanceService,
		@inject(CameraService) private cameraService: CameraService,
		@inject(CAMERA_CONTROL_SERVICE) @optional() private cameraControlService?: ICameraControlService,
	) {}

	public initialize(): void {
		const workspace3DAnchorPoint = this.mapConfigService.origin;
		this.metersPerLngDeg = getMetersPerLongitude(workspace3DAnchorPoint[1]);

		const camera = this.cameraService.camera;
		const map = this.mapLibreInstanceService.map;
		map.transform.setFov(camera.fov);

		// Disable all map interactions — Three.js camera controls take over
		map.dragPan.disable();
		map.dragRotate.disable();
		map.scrollZoom.disable();
		map.touchZoomRotate.disable();
		map.keyboard.disable();

		this.cameraControlService?.onCameraUpdated.subscribe(this.synchronizeCamera);
		this.synchronizeCamera({ camera });

		this.logger.log("initialized");
	}

	public dispose(): void {
		this.cameraControlService?.onCameraUpdated.unsubscribe(this.synchronizeCamera);
	}

	public synchronizeCamera = ({ camera }: { camera: THREE.PerspectiveCamera }) => {
		// Skip sync when camera is under ground - ground intersection produces invalid values that crash MapLibre.
		// If we look from below the map we don't want to see the map anyway, so this is not a problem.
		if (camera.position.y < 0) {
			return;
		}

		const mapInstance = this.mapLibreInstanceService.map;
		const workspace3DAnchorPoint = this.mapConfigService.origin;

		const { cameraForward, cameraUp } = getWorkspace3DCameraOrientationVectors({ camera });
		const { pitchDeg, bearingDeg, rollDeg } = getMapCameraOrientation({ cameraForward, cameraUp });
		const { point, distance } = getMapCameraTarget({ cameraForward, cameraPosition: camera.position });

		const mapCenterLng = workspace3DAnchorPoint[0] - point.x / this.metersPerLngDeg;
		const mapCenterLat = workspace3DAnchorPoint[1] + point.z / METERS_PER_LAT_DEG;

		const mapZoom = this.getComputedZoom(distance, camera.fov);

		mapInstance.jumpTo({
			center: [mapCenterLng, mapCenterLat],
			zoom: mapZoom,
			pitch: pitchDeg,
			bearing: bearingDeg,
			roll: rollDeg,
		});
	};

	/**
	 * Computes the MapLibre zoom level from camera distance and FOV.
	 *
	 * In web maps, at zoom level z the world is tileSize × 2^z pixels wide.
	 * Solving for z:
	 *
	 *   metersPerPixel = circumferenceAtLat / (tileSize × 2^z)
	 *   tileSize × 2^z = circumferenceAtLat / metersPerPixel
	 *   z = log2(circumferenceAtLat / (tileSize × metersPerPixel))
	 *
	 * @see https://wiki.openstreetmap.org/wiki/Zoom_levels
	 */
	private getComputedZoom(cameraDistanceToTargetPoint: number, workspace3DCameraFov: number): number {
		const lat = this.mapConfigService.origin[1];
		const cosLat = Math.cos(THREE.MathUtils.degToRad(lat));
		const circumferenceAtLat = EARTH_CIRCUMFERENCE_IN_METERS * cosLat;

		const viewportHeight = this.mapLibreInstanceService.map.getCanvas().clientHeight;
		const fovRad = THREE.MathUtils.degToRad(workspace3DCameraFov);

		// Visible height at target distance (perspective projection):
		//
		//     camera
		//    /|
		//   / |
		//  /  | distance
		// /   |
		// )α  |
		// \   |
		//  \  |                 tan(α) = (visibleHeight / 2) / distance
		//   \ |                 visibleHeight = 2 * distance * tan(α)
		//    \|
		//
		// where α = fov / 2
		// @see https://en.wikipedia.org/wiki/Viewing_frustum
		const visibleHeight = 2 * cameraDistanceToTargetPoint * Math.tan(fovRad / 2);

		const metersPerPixel = visibleHeight / viewportHeight;
		const tileSize = this.mapLibreInstanceService.map.transform.tileSize;
		const tileSizeInMeters = tileSize * metersPerPixel;

		return Math.log2(circumferenceAtLat / tileSizeInMeters);
	}
}

export { Workspace3DToMapCameraSyncService };
